import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ActiveFilter,
  DateRangeValue,
  FilterFieldConfig,
  FilterOption,
  MasterFilterPersistence,
  MasterFilterValues,
  UseMasterFilterReturn,
} from './types';
import { readValuesFromUrl, usePersistFilterValues } from './urlPersistence';

/** Build the initial values map from config defaults. */
const buildDefaults = (config: FilterFieldConfig[]): MasterFilterValues => {
  const defaults: MasterFilterValues = {};
  for (const field of config) {
    if (field.defaultValue !== undefined) {
      defaults[field.key] = field.defaultValue;
    }
  }
  return defaults;
};

/** Check whether a value is "empty" (no filter applied). */
const isEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  // DateRangeValue
  if (typeof value === 'object' && value !== null && 'start' in value && 'end' in value) {
    const dr = value as DateRangeValue;
    return dr.start === null && dr.end === null;
  }
  return false;
};

/** Produce a human-readable display string for a filter value. */
const formatDisplayValue = (value: unknown, config: FilterFieldConfig, dynOpts?: FilterOption[]): string => {
  const allOptions = [...(config.options ?? []), ...(dynOpts ?? [])];

  if (Array.isArray(value)) {
    if (value.length <= 2) {
      return value.map((v) => allOptions.find((o) => o.value === v)?.label ?? v).join(', ');
    }
    return `${value.length} seleccionados`;
  }

  if (typeof value === 'object' && value !== null && 'start' in value && 'end' in value) {
    const dr = value as DateRangeValue;
    const parts: string[] = [];
    if (dr.start) parts.push(dr.start.format('DD/MM/YY'));
    if (dr.end) parts.push(dr.end.format('DD/MM/YY'));
    return parts.join(' – ');
  }

  if (typeof value === 'string') {
    const match = allOptions.find((o) => o.value === value);
    if (match) return match.label;
    return value;
  }

  return String(value);
};

export function useMasterFilter(
  config: FilterFieldConfig[],
  businessUnitId: number | null,
  dynamicOptions?: Record<string, FilterOption[]>,
  initialValues?: MasterFilterValues,
  persistence?: MasterFilterPersistence
): UseMasterFilterReturn {
  const persistNs = persistence?.urlNamespace;

  // Initial values: defaults < explicit initialValues < URL-restored values (highest priority).
  const [values, setValues] = useState<MasterFilterValues>(() => ({
    ...buildDefaults(config),
    ...(initialValues ?? {}),
    ...(persistNs ? readValuesFromUrl(config, persistNs) : {}),
  }));
  const [modalOpen, setModalOpen] = useState(false);
  const prevBuId = useRef(businessUnitId);

  // Reset all filters when businessUnitId changes.
  // Skip the first null/-1 → real-id transition: the sidebar context hydrates async on mount,
  // so that "change" is not a user action — and resetting wipes URL-seeded values (e.g.
  // deep-links from the dashboard Sankey: `leads.dateRange.*`, `leads.sources`, etc.).
  useEffect(() => {
    const prev = prevBuId.current;
    if (prev === businessUnitId) return;
    prevBuId.current = businessUnitId;
    const isHydration = prev === null || prev === -1;
    if (isHydration) return;
    setValues(buildDefaults(config));
  }, [businessUnitId, config]);

  // Auto-clear hidden fields when visibleWhen predicate becomes false
  useEffect(() => {
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of config) {
        if (field.visibleWhen && !field.visibleWhen(prev) && !isEmpty(prev[field.key])) {
          next[field.key] = field.defaultValue ?? undefined;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [values, config]);

  usePersistFilterValues(config, persistNs, values, setValues);

  const setValue = useCallback((key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearOne = useCallback(
    (key: string) => {
      const field = config.find((f) => f.key === key);
      setValues((prev) => ({ ...prev, [key]: field?.defaultValue ?? undefined }));
    },
    [config]
  );

  const clearAll = useCallback(() => {
    setValues(buildDefaults(config));
  }, [config]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  // Derive active (non-empty, visible) filters for chip display
  const activeFilters: ActiveFilter[] = useMemo(() => {
    const result: ActiveFilter[] = [];
    for (const field of config) {
      // Skip hidden fields
      if (field.visibleWhen && !field.visibleWhen(values)) continue;
      const val = values[field.key];
      if (isEmpty(val)) continue;
      result.push({
        key: field.key,
        label: field.label,
        displayValue: formatDisplayValue(val, field, dynamicOptions?.[field.key]),
      });
    }
    return result;
  }, [config, values, dynamicOptions]);

  return {
    config,
    values,
    setValue,
    clearOne,
    clearAll,
    activeFilters,
    modalOpen,
    openModal,
    closeModal,
  };
}
